import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './ProgressiveCoinReveal.module.css';
import { getRevealPercent } from '../../utils/revealPercent';

export default function ProgressiveCoinReveal({
                                                  coinImageUrl,
                                                  coinName = 'Reward Coin',
                                                  verified = 0,
                                                  required = 1,
                                                  size = 80,
                                                  bgColor = '#111',
                                                  onTap,
                                                  nextTitle,
                                                  onComplete,
                                              }) {
    // Clamp so verified never exceeds required
    const clampedVerified = Math.min(verified, required);
    const revealPct = getRevealPercent(clampedVerified, required);
    const isComplete = revealPct >= 1;
    const revealPercent = Math.round(revealPct * 100);

    // ── Animated reveal — CSS transition drives the mask gradient ──────────
    // We track the "current displayed" percent separately so we can animate
    // forward-only (no animation on initial load for completed collections).
    const [displayPct, setDisplayPct] = useState(revealPercent);
    const prevRevealPct = useRef(revealPercent);
    const [animateMask, setAnimateMask] = useState(false);

    useEffect(() => {
        const prev = prevRevealPct.current;
        if (revealPercent > prev) {
            // Progress advanced — enable CSS transition then update
            setAnimateMask(true);
            setDisplayPct(revealPercent);
        } else if (revealPercent !== prev) {
            // Regressed or same — snap without animation
            setAnimateMask(false);
            setDisplayPct(revealPercent);
        }
        prevRevealPct.current = revealPercent;
    }, [revealPercent]);

    // ── Celebration ─────────────────────────────────────────────────────────
    const [celebrating, setCelebrating] = useState(false);
    const [shimmer, setShimmer] = useState(false);
    const wasComplete = useRef(isComplete);

    useEffect(() => {
        if (!wasComplete.current && isComplete) {
            setCelebrating(true);
            setShimmer(true);
            const shimmerTimer = setTimeout(() => setShimmer(false), 1200);
            const celebTimer   = setTimeout(() => {
                setCelebrating(false);
                onComplete?.();
            }, 1000);
            return () => {
                clearTimeout(shimmerTimer);
                clearTimeout(celebTimer);
            };
        }
        wasComplete.current = isComplete;
    }, [isComplete, onComplete]);

    // ── Tooltip ─────────────────────────────────────────────────────────────
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const tooltipTimer = useRef(null);

    const handleClick = useCallback(() => {
        if (onTap) { onTap(); return; }
        if (isComplete) return;
        setTooltipVisible(true);
        clearTimeout(tooltipTimer.current);
        tooltipTimer.current = setTimeout(() => setTooltipVisible(false), 2800);
    }, [isComplete, onTap]);

    useEffect(() => () => clearTimeout(tooltipTimer.current), []);

    // ── Derived values ───────────────────────────────────────────────────────
    const pct = displayPct; // the value driving the mask (may be animating via CSS)

    // Glow: hide at 0% progress (≤4%) and at 100% (complete)
    const showGlow = revealPct > 0.04 && revealPct < 1;

    // Glow sits in local coin coordinate space, centered on the reveal boundary.
    // bottom = revealPct × size, offset by half the glow height (14px) to center it.
    const glowBottom = `calc(${pct}% - 14px)`;

    return (
        <div
            className={styles.wrapper}
            style={{ width: size, height: size }}
            onClick={handleClick}
            role={isComplete ? undefined : 'button'}
            aria-label={isComplete ? coinName : `${coinName} — ${pct}% revealed`}
        >
            {/* Coin image — base opacity 0.12 when unrevealed so silhouette shows */}
            <img
                src={coinImageUrl}
                alt={coinName}
                className={`${styles.coinImg} ${celebrating ? styles.pulse : ''} ${!isComplete ? styles.unrevealedBase : ''}`}
                style={{ width: size, height: size }}
            />

            {/* Soft gradient mask — multi-stop fade zone spans ~18% of coin height */}
            {!isComplete && (
                <div
                    className={`${styles.mask} ${animateMask ? styles.maskAnimated : ''}`}
                    style={{
                        background: `linear-gradient(
                            to top,
                            transparent 0%,
                            transparent ${pct}%,
                            rgba(0,0,0,0.75) calc(${pct}% + 8%),
                            rgba(0,0,0,0.92) calc(${pct}% + 18%),
                            rgba(0,0,0,0.98) 100%
                        )`,
                    }}
                />
            )}

            {/* Golden glow at the reveal boundary */}
            {showGlow && (
                <div
                    className={styles.glow}
                    style={{ bottom: glowBottom }}
                />
            )}

            {/* Shimmer overlay on completion */}
            {shimmer && <div className={styles.shimmer} />}

            {/* Tooltip */}
            {tooltipVisible && (
                <div className={styles.tooltip}>
                    {nextTitle
                        ? `Verify "${nextTitle}" to reveal more`
                        : 'Verify more titles to reveal the coin'}
                </div>
            )}
        </div>
    );
}